<template>
  <form @submit.prevent="updateSignature()">
    <div class="profile--settings--row text-black-900 dark:text-slate-300 row">
      <div class="w-[25%] py-4 pr-6 ml-0">
        <h4 class="block-title text-black-900 dark:text-slate-200">
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE') }}
        </h4>
        <p>{{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE') }}</p>
      </div>
      <div class="p-4 w-[45%]">
        <div class="signature-logo-section">
          <label>
            {{
              $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_LABEL'
              )
            }}
          </label>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0 mb-2">
            {{
              $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_NOTE')
            }}
          </p>
          <div class="signature-logo-row">
            <img
              v-if="logoPreviewUrl"
              :src="logoPreviewUrl"
              alt="signature logo"
              class="signature-logo-preview"
            />
            <input
              ref="signatureLogoInput"
              type="file"
              accept="image/png, image/jpeg"
              class="signature-logo-input"
              @change="handleLogoUpload"
            />
          </div>
          <div v-if="hasSavedLogo" class="signature-logo-actions">
            <woot-button
              type="button"
              color-scheme="alert"
              variant="hollow"
              size="tiny"
              @click="deleteSignatureLogo"
            >
              {{
                $t(
                  'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_DELETE'
                )
              }}
            </woot-button>
          </div>
        </div>

        <div>
          <label for="message-signature-input">{{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.LABEL')
          }}</label>
          <woot-message-editor
            id="message-signature-input"
            v-model="messageSignature"
            class="message-editor"
            :disable-autofocus="suppressEditorAutofocus"
            :is-format-mode="true"
            :placeholder="
              $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')
            "
            @blur="$v.messageSignature.$touch"
          />
        </div>
        <woot-button
          :is-loading="isUpdating"
          type="submit"
          :is-disabled="$v.messageSignature.$invalid"
        >
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT') }}
        </woot-button>
      </div>
    </div>
  </form>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';

import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import alertMixin from 'shared/mixins/alertMixin';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const SIGNATURE_LOGO_MAX_MB = 5;
const SIGNATURE_LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg'];

export default {
  components: {
    WootMessageEditor,
  },
  mixins: [alertMixin],
  data() {
    return {
      messageSignature: '',
      enableMessageSignature: false,
      isUpdating: false,
      errorMessage: '',
      signatureLogoFile: null,
      signatureLogoPreview: '',
    };
  },
  validations: {
    messageSignature: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
    }),
    // When opening profile settings to add event types, focus must go to Event ID field, not signature editor
    suppressEditorAutofocus() {
      return this.$route.query.focus === 'event-types';
    },
    savedLogoUrl() {
      return this.currentUser.signature_logo_url || '';
    },
    hasSavedLogo() {
      return !!this.savedLogoUrl && !this.signatureLogoFile;
    },
    logoPreviewUrl() {
      return this.signatureLogoPreview || this.savedLogoUrl;
    },
  },
  watch: {
    // Watch the specific field instead of the whole currentUser object.
    // currentUser is replaced on every presence/availability websocket update,
    // which would otherwise overwrite the in-flight editor content with the
    // last saved signature while the user is still typing.
    'currentUser.message_signature': function onSignatureChange() {
      this.initValues();
    },
    'currentUser.signature_logo_url': function onLogoChange() {
      this.clearLocalLogoSelection();
    },
  },
  mounted() {
    this.initValues();
  },
  methods: {
    initValues() {
      const { message_signature: messageSignature } = this.currentUser;
      const nextValue = messageSignature || '';
      if (nextValue !== this.messageSignature) {
        this.messageSignature = nextValue;
      }
    },
    handleLogoUpload(event) {
      const [file] = event.target.files;
      if (!file) return;

      if (!SIGNATURE_LOGO_ALLOWED_TYPES.includes(file.type)) {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_TYPE_ERROR'
          )
        );
        if (this.$refs.signatureLogoInput) {
          this.$refs.signatureLogoInput.value = '';
        }
        return;
      }

      if (!checkFileSizeLimit(file, SIGNATURE_LOGO_MAX_MB)) {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_SIZE_ERROR',
            { MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: SIGNATURE_LOGO_MAX_MB }
          )
        );
        if (this.$refs.signatureLogoInput) {
          this.$refs.signatureLogoInput.value = '';
        }
        return;
      }

      this.signatureLogoFile = file;
      this.signatureLogoPreview = URL.createObjectURL(file);
    },
    clearLocalLogoSelection() {
      this.signatureLogoFile = null;
      if (this.signatureLogoPreview) {
        URL.revokeObjectURL(this.signatureLogoPreview);
      }
      this.signatureLogoPreview = '';
      if (this.$refs.signatureLogoInput) {
        this.$refs.signatureLogoInput.value = '';
      }
    },
    async deleteSignatureLogo() {
      try {
        await this.$store.dispatch('deleteSignatureLogo');
        this.clearLocalLogoSelection();
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_DELETE_SUCCESS'
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LOGO_DELETE_FAILED'
          )
        );
      }
    },
    async updateSignature() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }

      this.isUpdating = true;
      try {
        const payload = { message_signature: this.messageSignature };
        if (this.signatureLogoFile) {
          payload.signatureLogo = this.signatureLogoFile;
        }
        await this.$store.dispatch('updateProfile', payload);
        this.clearLocalLogoSelection();
        this.errorMessage = this.$t(
          'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
        );
      } catch (error) {
        this.errorMessage = this.$t(
          'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
        );
        if (error?.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        }
      } finally {
        this.isUpdating = false;
        this.showAlert(this.errorMessage);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile--settings--row {
  .ProseMirror-woot-style {
    @apply h-20;
  }
}

.message-editor {
  @apply px-3 mb-4;

  :deep {
    .ProseMirror-menubar {
      position: static !important;
      z-index: auto;
      overflow: hidden;
      max-height: 40px;
    }
  }
}

.signature-logo-section {
  @apply mb-4;
}

.signature-logo-row {
  @apply flex items-center gap-3 flex-wrap;
}

.signature-logo-preview {
  @apply max-h-16 max-w-[160px] object-contain rounded-sm border border-solid border-slate-100 dark:border-slate-700 bg-white p-1;
}

.signature-logo-input {
  @apply mb-0;
}

.signature-logo-actions {
  @apply mt-2;
}
</style>
